import React, { useEffect, useState } from "react";
import "./index.css";
import Overlay from "../Overlay";

const TEMPLATES = [
    { id: "refined",   name: "Refined",       description: "Classic single-column with refined typography and a coral accent. The default — recruiter-familiar, ATS-friendly." },
    { id: "twocolumn", name: "Two-Column",    description: "Contact, personal details, licenses and skills sit in a left rail. Experience and education on the right." },
];

const RefinedPreview = () => (
    <div className="preview-refined">
        <div className="preview-refined-header">
            <div className="preview-refined-name-stack">
                <div className="preview-refined-name" />
                <div className="preview-refined-role" />
            </div>
            <div className="preview-refined-photo" />
        </div>
        <div className="preview-section">
            <div className="preview-section-bar" />
            <div className="preview-section-label" />
            <div className="preview-section-rule" />
        </div>
        <div className="preview-line" />
        <div className="preview-line preview-line--short" />
        <div className="preview-section">
            <div className="preview-section-bar" />
            <div className="preview-section-label" />
            <div className="preview-section-rule" />
        </div>
        <div className="preview-line" />
        <div className="preview-line" />
        <div className="preview-line preview-line--short" />
        <div className="preview-section">
            <div className="preview-section-bar" />
            <div className="preview-section-label" />
            <div className="preview-section-rule" />
        </div>
        <div className="preview-line" />
        <div className="preview-line preview-line--short" />
    </div>
);

const TwoColumnPreview = () => (
    <div className="preview-twocol">
        <div className="preview-twocol-rail">
            <div className="preview-twocol-rail-photo" />
            <div className="preview-twocol-rail-bar" />
            <div className="preview-line" />
            <div className="preview-line preview-line--short" />
            <div className="preview-twocol-rail-bar" />
            <div className="preview-line" />
            <div className="preview-line preview-line--short" />
            <div className="preview-twocol-rail-bar" />
            <div className="preview-line" />
        </div>
        <div className="preview-twocol-body">
            <div className="preview-twocol-name" />
            <div className="preview-twocol-role" />
            <div className="preview-section">
                <div className="preview-section-bar" />
                <div className="preview-section-label" />
                <div className="preview-section-rule" />
            </div>
            <div className="preview-line" />
            <div className="preview-line preview-line--short" />
            <div className="preview-section">
                <div className="preview-section-bar" />
                <div className="preview-section-label" />
                <div className="preview-section-rule" />
            </div>
            <div className="preview-line" />
            <div className="preview-line" />
            <div className="preview-line preview-line--short" />
        </div>
    </div>
);

const previews = {
    refined: <RefinedPreview />,
    twocolumn: <TwoColumnPreview />,
};

const OPTIONAL_SECTIONS = [
    { key: "objective", label: "Objective" },
    { key: "equipment", label: "Equipment Knowledge" },
    { key: "hobbies",   label: "Interests & Hobbies" },
    { key: "references", label: "References" },
];

export const ACCENT_COLORS = {
    brand: { label: "Brand",  hex: "#E85533" },
    navy:  { label: "Navy",   hex: "#1F5C8C" },
    mono:  { label: "Mono",   hex: "#3E4554" },
};

export default function CVTemplatePicker({ show, busy, onCancel, onConfirm }) {

    const [selected, setSelected] = useState("refined");
    const [accent, setAccent] = useState("brand");
    const [sections, setSections] = useState({
        objective: true,
        equipment: true,
        hobbies: true,
        references: true,
    });

    useEffect(() => {
        if (!show) return;
        const onKey = (e) => {
            if (e.key === "Escape" && onCancel) onCancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [show, onCancel]);

    if (!show) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && onCancel) onCancel();
    };

    const toggleSection = (key) => {
        setSections((s) => ({ ...s, [key]: !s[key] }));
    };

    return (
        <Overlay>
            <div className="cv-picker-positioner" onClick={handleBackdropClick}>
                <div className="cv-picker-modal" role="dialog" aria-modal="true" aria-labelledby="cv-picker-title">
                    <div className="cv-picker-header">
                        <div>
                            <h2 className="cv-picker-title" id="cv-picker-title">Download your CV</h2>
                            <p className="cv-picker-subtitle">Choose a layout, colour and sections. It downloads as a polished PDF you can use for any application, on J4Crew or anywhere else.</p>
                        </div>
                        <button type="button" className="cv-picker-close" onClick={onCancel} aria-label="Close">×</button>
                    </div>

                    <div className="cv-picker-grid">
                        {TEMPLATES.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`cv-picker-card${selected === t.id ? " is-selected" : ""}`}
                                onClick={() => setSelected(t.id)}
                                aria-pressed={selected === t.id}
                            >
                                <div className="cv-picker-card-preview">{previews[t.id]}</div>
                                <p className="cv-picker-card-title">{t.name}</p>
                                <p className="cv-picker-card-desc">{t.description}</p>
                            </button>
                        ))}
                    </div>

                    <div className="cv-picker-accents">
                        <p className="cv-picker-accents-title">Accent colour</p>
                        <p className="cv-picker-accents-desc">Tints the section bars, role tag, and bullets. Body text stays the same.</p>
                        <div className="cv-picker-accents-row">
                            {Object.entries(ACCENT_COLORS).map(([key, { label, hex }]) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`cv-picker-accent${accent === key ? " is-selected" : ""}`}
                                    onClick={() => setAccent(key)}
                                    aria-pressed={accent === key}
                                >
                                    <span className="cv-picker-accent-swatch" style={{ backgroundColor: hex }} />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="cv-picker-sections">
                        <p className="cv-picker-sections-title">Sections to include</p>
                        <p className="cv-picker-sections-desc">Optional sections — untick to hide on this download. Always-on sections (Personal Details, Profile, Experience, Education, Licences, Skills) appear when there's data.</p>
                        <div className="cv-picker-sections-grid">
                            {OPTIONAL_SECTIONS.map((s) => (
                                <label key={s.key} className="cv-picker-check">
                                    <input type="checkbox" checked={!!sections[s.key]} onChange={() => toggleSection(s.key)} />
                                    <span>{s.label}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="cv-picker-actions">
                        <button type="button" className="cv-picker-btn cv-picker-btn--secondary" onClick={onCancel} disabled={busy}>Cancel</button>
                        <button type="button" className="cv-picker-btn cv-picker-btn--primary" onClick={() => onConfirm(selected, sections, ACCENT_COLORS[accent].hex)} disabled={busy}>
                            {busy ? "Generating…" : "Download"}
                        </button>
                    </div>
                </div>
            </div>
        </Overlay>
    );
}
